<!--
  @component AuthGate
  @description Authentication gate for Preparo when running in proxy mode

  When proxy mode is enabled (via Spectral Cloud Portal), this component
  checks if the user is authenticated and redirects to the Portal if not.

  In non-proxy mode, it simply renders the children without any checks.

  Also initializes Zotero if the API key is configured.
-->
<script lang="ts">
  import { onMount } from 'svelte';
  import {
    isAuthenticated,
    isProxyMode,
    isAuthLoading,
    isZoteroConfigured,
    redirectToPortal,
    initAuth,
  } from '@stores/authStore';
  import { zoteroActions } from '@stores/zoteroStore';
  import { i18n } from '@stores/i18nStore';

  // State
  let initialized = false;
  let redirecting = false;
  let zoteroInitialized = false;

  onMount(async () => {
    // Initialize authentication (handles SSO tokens from URL)
    await initAuth();
    initialized = true;

    // If in proxy mode and not authenticated, redirect to Portal
    if ($isProxyMode && !$isAuthenticated && !redirecting) {
      redirecting = true;
      // Small delay to show the message before redirecting
      setTimeout(() => {
        redirectToPortal();
      }, 1500);
    }
  });

  // Reactive check for post-init auth changes
  $: if (initialized && $isProxyMode && !$isAuthenticated && !redirecting) {
    redirecting = true;
    setTimeout(() => {
      redirectToPortal();
    }, 1500);
  }

  // Initialize Zotero when configured (in proxy mode: after auth, in standalone: immediately)
  $: if (initialized && $isZoteroConfigured && !zoteroInitialized) {
    // In proxy mode, wait for authentication
    if ($isProxyMode && !$isAuthenticated) {
      // Wait for auth
    } else {
      zoteroInitialized = true;
      // Initialize in background (don't block rendering)
      zoteroActions.init().catch((err) => {
        console.error('Failed to initialize Zotero:', err);
      });
    }
  }
</script>

{#if !$isProxyMode}
  <!-- Non-proxy mode: render children directly -->
  <slot />
{:else if $isAuthLoading || !initialized}
  <!-- Loading state -->
  <div class="auth-gate">
    <div class="auth-gate-content">
      <div class="spinner"></div>
      <p>{$i18n('auth.checking')}</p>
    </div>
  </div>
{:else if !$isAuthenticated}
  <!-- Not authenticated: show redirect message -->
  <div class="auth-gate">
    <div class="auth-gate-content">
      <div class="auth-icon">🔒</div>
      <h2>{$i18n('auth.loginRequired')}</h2>
      <p>{$i18n('auth.redirecting')}</p>
      <div class="spinner"></div>
      <button class="login-btn" on:click={redirectToPortal}>
        {$i18n('auth.loginWithPortal')}
      </button>
    </div>
  </div>
{:else}
  <!-- Authenticated: render children -->
  <slot />
{/if}

<style>
  .auth-gate {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    background: var(--app-background, #f9fafb);
  }

  .auth-gate-content {
    text-align: center;
    padding: 2rem;
    max-width: 400px;
  }

  .auth-icon {
    font-size: 3rem;
    margin-bottom: 1rem;
  }

  .auth-gate-content h2 {
    margin: 0 0 0.5rem;
    color: var(--text-primary, #1f2937);
    font-size: 1.5rem;
    font-weight: 600;
  }

  .auth-gate-content p {
    margin: 0 0 1.5rem;
    color: var(--text-muted, #6b7280);
    font-size: 1rem;
  }

  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid var(--border-color, #e5e7eb);
    border-top-color: var(--accent-color, #3b82f6);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 1rem auto;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .login-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.5rem;
    background: var(--accent-color, #3b82f6);
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s;
    margin-top: 1rem;
  }

  .login-btn:hover {
    background: var(--accent-color-hover, #2563eb);
  }

  /* Dark mode support */
  :global([data-theme='dark']) .auth-gate {
    background: var(--app-background);
  }

  :global([data-theme='dark']) .auth-gate-content h2 {
    color: var(--text-primary);
  }

  :global([data-theme='dark']) .auth-gate-content p {
    color: var(--text-muted);
  }

  @media (prefers-color-scheme: dark) {
    .auth-gate {
      background: var(--app-background);
    }

    .auth-gate-content h2 {
      color: var(--text-primary);
    }

    .auth-gate-content p {
      color: var(--text-muted);
    }
  }
</style>
